import {
  faCheck,
  faExternalLink,
  faTimes
} from '@fortawesome/pro-regular-svg-icons'
import { faDoNotEnter } from '@fortawesome/pro-solid-svg-icons'
import { useMemo } from 'react'
import Link from 'next/link'
import Thumbnail from 'components/Thumbnail'
import {
  CollectiveProposalVoteFieldsFragment,
  VoteType
} from 'graphql/generated'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import EtherscanLink, { calculateShortAddress } from '../Common/EtherscanLink'
import { formatToken } from 'lib/collectives/helpers'
import { DemoTooltipText } from 'context/CollectiveDemoContext'
import { useCollectiveContext } from 'context/CollectiveContext'
import StyledTooltip from 'components/Tooltip/Tooltip'
import Typography from 'components/common/Typography'
import { useBreakpoint } from 'hooks/useBreakpoint'

const getEffectiveName = (vote: CollectiveProposalVoteFieldsFragment) =>
  vote.member.user.platformUser
    ? vote.member.user.platformUser.displayName
    : vote.member.user.address

interface IProps {
  vote: CollectiveProposalVoteFieldsFragment
  formatWeight: boolean
  showVotes: boolean
  showSignature: boolean
}

function VoteDetailHeaders({
  showVotes,
  showSignature
}: Pick<IProps, 'showSignature' | 'showVotes'>) {
  return (
    <>
      {showVotes && (
        <>
          <th className="py-2.5 px-5 uppercase text-center text-gray-500 text-xs font-medium">
            Vote
          </th>
          <th className="py-2.5 px-5 uppercase text-center text-gray-500 text-xs font-medium">
            Weight
          </th>
        </>
      )}

      {showSignature && (
        <th className="py-2.5 px-5 uppercase text-center text-gray-500 text-xs font-medium">
          Signature
        </th>
      )}
    </>
  )
}

function VoteDetails({ vote, showVotes, showSignature, formatWeight }: IProps) {
  const { demoMode } = useCollectiveContext()
  const voteIcon = useMemo(
    () =>
      vote.type === VoteType.FOR
        ? faCheck
        : vote.type === VoteType.AGAINST
        ? faTimes
        : faDoNotEnter,
    [vote.type]
  )

  return (
    <>
      {showVotes && (
        <>
          <td className="px-3 sm:py-3 max-sm:w-1/3">
            <div className="flex sm:flex-col text-center justify-center gap-1 items-center">
              <FontAwesomeIcon
                icon={voteIcon}
                size={'1x'}
                className={'text-gray-900'}
              />
              <p className="text-xs font-light text-gray-500 pt-1 uppercase">
                {vote.type}
              </p>
            </div>
          </td>

          <td className="px-3 sm:py-3 text-center max-sm:w-1/3">
            <p className="text-sm font-medium text-gray-700">
              {formatWeight ? formatToken(vote.weight) : vote.weight}
            </p>
          </td>
        </>
      )}

      {showSignature && (
        <td className="px-3 sm:py-3 text-center max-sm:w-1/3">
          {!demoMode ? (
            vote.signatureUrl ? (
              <Link href={vote.signatureUrl} passHref>
                <Typography
                  component={'p'}
                  newTab
                  size="sm"
                  fontWeight="medium"
                  color="darkGray"
                >
                  {`View `}
                  <FontAwesomeIcon
                    icon={faExternalLink}
                    size={'xs'}
                    className="text-gray-700"
                  />
                </Typography>
              </Link>
            ) : null
          ) : (
            <StyledTooltip title={DemoTooltipText} arrow>
              <div className="flex cursor-pointer">
                <Typography size="sm" fontWeight="medium" color="darkGray">
                  {`View `}
                  <FontAwesomeIcon
                    icon={faExternalLink}
                    size={'xs'}
                    className="text-gray-700"
                  />
                </Typography>
              </div>
            </StyledTooltip>
          )}
        </td>
      )}
    </>
  )
}

function VoteListItem(props: IProps) {
  const { vote } = props
  const effectiveName = getEffectiveName(vote)
  const { isSm } = useBreakpoint()

  return (
    <tr>
      <td className="flex flex-col sm:flex-row sm:items-center py-3">
        <div className="flex space-x-2 py-1 sm:py-0 px-3">
          <Thumbnail
            src={vote.member.user.platformUser?.thumb}
            className="h-10 w-10 rounded-full overflow-hidden"
          />

          <Typography size="sm" className=" mb-2 sm:mb-0">
            {effectiveName}{' '}
            {vote.masterVote ? (
              <Typography
                fontWeight="light"
                size="xs"
                component={'span'}
                className="break-all"
              >
                delegated vote cast by {getEffectiveName(vote.masterVote)} (
                {calculateShortAddress(vote.masterVote.member.user.address)})
              </Typography>
            ) : null}
            <EtherscanLink
              shortAddress
              type={'address'}
              address={vote.member.user.address}
            />
          </Typography>
        </div>

        {!isSm && (
          <table className="w-full">
            <thead>
              <tr>
                <VoteDetailHeaders {...props} />
              </tr>
            </thead>
            <tbody>
              <tr>
                <VoteDetails {...props} />
              </tr>
            </tbody>
          </table>
        )}
      </td>

      {isSm && <VoteDetails {...props} />}
    </tr>
  )
}

export default VoteListItem
